// Predefined diagram templates for LearSpace Draw
import { v4 as uuidv4 } from 'uuid';

// Template categories
export const TEMPLATE_CATEGORIES = [
  { id: 'kubernetes', name: 'Kubernetes', color: '#326CE5', icon: '☸️' },
  { id: 'cicd', name: 'CI/CD Pipelines', color: '#E34F26', icon: '🔄' },
  { id: 'aws', name: 'AWS Architecture', color: '#FF9900', icon: '☁️' },
  { id: 'gcp', name: 'Google Cloud', color: '#4285F4', icon: '🌐' },
  { id: 'azure', name: 'Microsoft Azure', color: '#0089D6', icon: '⚡' },
  { id: 'devops', name: 'DevOps General', color: '#00ADD8', icon: '🛠️' },
  { id: 'networking', name: 'Networking', color: '#7B42BC', icon: '🌍' },
  { id: 'monitoring', name: 'Monitoring', color: '#E6522C', icon: '📊' },
];

// Helper to create element with unique ID
const el = (type, props) => ({
  id: uuidv4(),
  type,
  rotation: 0,
  locked: false,
  visible: true,
  opacity: 1,
  strokeColor: '#1e1e1e',
  fillColor: 'transparent',
  strokeWidth: 2,
  fontSize: 16,
  fontFamily: 'Inter, sans-serif',
  ...props,
  createdAt: Date.now(),
  updatedAt: Date.now(),
});

// ============ KUBERNETES TEMPLATES ============

const k8sBasicCluster = {
  id: 'k8s-basic-cluster',
  name: 'Cluster Kubernetes Basic',
  description: 'Architecture de base d\'un cluster Kubernetes avec Master et Workers',
  category: 'kubernetes',
  thumbnail: '/templates/k8s-basic.png',
  elements: [
    // Title
    el('text', { x: 350, y: 30, width: 300, height: 40, text: 'Kubernetes Cluster', fontSize: 28, strokeColor: '#326CE5' }),
    
    // Master Node Box
    el('rectangle', { x: 50, y: 100, width: 300, height: 250, fillColor: '#E3F2FD', strokeColor: '#326CE5', strokeWidth: 3 }),
    el('text', { x: 150, y: 120, width: 100, height: 24, text: 'Master Node', fontSize: 18, strokeColor: '#326CE5' }),
    
    // Master components
    el('rectangle', { x: 70, y: 160, width: 120, height: 50, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 90, y: 180, width: 80, height: 20, text: 'API Server', fontSize: 12 }),
    
    el('rectangle', { x: 210, y: 160, width: 120, height: 50, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 230, y: 180, width: 80, height: 20, text: 'Scheduler', fontSize: 12 }),
    
    el('rectangle', { x: 70, y: 230, width: 120, height: 50, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 80, y: 250, width: 100, height: 20, text: 'Controller Mgr', fontSize: 12 }),
    
    el('rectangle', { x: 210, y: 230, width: 120, height: 50, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 250, y: 250, width: 50, height: 20, text: 'etcd', fontSize: 12 }),
    
    // Worker Nodes
    el('rectangle', { x: 400, y: 100, width: 200, height: 180, fillColor: '#E8F5E9', strokeColor: '#4CAF50', strokeWidth: 2 }),
    el('text', { x: 450, y: 120, width: 100, height: 24, text: 'Worker Node 1', fontSize: 14, strokeColor: '#4CAF50' }),
    el('rectangle', { x: 420, y: 150, width: 70, height: 40, fillColor: '#C8E6C9', strokeColor: '#388E3C' }),
    el('text', { x: 435, y: 165, width: 50, height: 16, text: 'Pod', fontSize: 11 }),
    el('rectangle', { x: 510, y: 150, width: 70, height: 40, fillColor: '#C8E6C9', strokeColor: '#388E3C' }),
    el('text', { x: 525, y: 165, width: 50, height: 16, text: 'Pod', fontSize: 11 }),
    el('rectangle', { x: 420, y: 210, width: 160, height: 30, fillColor: '#A5D6A7', strokeColor: '#388E3C' }),
    el('text', { x: 465, y: 220, width: 80, height: 16, text: 'Kubelet', fontSize: 11 }),
    
    el('rectangle', { x: 620, y: 100, width: 200, height: 180, fillColor: '#E8F5E9', strokeColor: '#4CAF50', strokeWidth: 2 }),
    el('text', { x: 670, y: 120, width: 100, height: 24, text: 'Worker Node 2', fontSize: 14, strokeColor: '#4CAF50' }),
    el('rectangle', { x: 640, y: 150, width: 70, height: 40, fillColor: '#C8E6C9', strokeColor: '#388E3C' }),
    el('text', { x: 655, y: 165, width: 50, height: 16, text: 'Pod', fontSize: 11 }),
    el('rectangle', { x: 730, y: 150, width: 70, height: 40, fillColor: '#C8E6C9', strokeColor: '#388E3C' }),
    el('text', { x: 745, y: 165, width: 50, height: 16, text: 'Pod', fontSize: 11 }),
    el('rectangle', { x: 640, y: 210, width: 160, height: 30, fillColor: '#A5D6A7', strokeColor: '#388E3C' }),
    el('text', { x: 685, y: 220, width: 80, height: 16, text: 'Kubelet', fontSize: 11 }),
    
    // Arrows from Master to Workers
    el('arrow', { x: 350, y: 200, width: 50, height: 0, strokeColor: '#326CE5', strokeWidth: 2 }),
    el('arrow', { x: 350, y: 200, width: 270, height: 0, strokeColor: '#326CE5', strokeWidth: 2 }),
  ],
};

const k8sWithIngress = {
  id: 'k8s-with-ingress',
  name: 'Kubernetes avec Ingress',
  description: 'Architecture K8s avec Ingress Controller et Load Balancer',
  category: 'kubernetes',
  thumbnail: '/templates/k8s-ingress.png',
  elements: [
    // Title
    el('text', { x: 300, y: 20, width: 400, height: 40, text: 'Kubernetes + Ingress Architecture', fontSize: 24, strokeColor: '#326CE5' }),
    
    // Internet cloud
    el('circle', { x: 350, y: 70, width: 100, height: 60, fillColor: '#FFF3E0', strokeColor: '#FF9800' }),
    el('text', { x: 375, y: 95, width: 50, height: 20, text: 'Internet', fontSize: 12, strokeColor: '#FF9800' }),
    
    // Arrow down
    el('arrow', { x: 400, y: 130, width: 0, height: 40, strokeColor: '#666' }),
    
    // Load Balancer
    el('rectangle', { x: 300, y: 180, width: 200, height: 50, fillColor: '#E3F2FD', strokeColor: '#2196F3', strokeWidth: 2 }),
    el('text', { x: 350, y: 200, width: 100, height: 20, text: 'Load Balancer', fontSize: 14, strokeColor: '#2196F3' }),
    
    // Arrow down
    el('arrow', { x: 400, y: 230, width: 0, height: 30, strokeColor: '#666' }),
    
    // Ingress Controller
    el('rectangle', { x: 280, y: 270, width: 240, height: 60, fillColor: '#FCE4EC', strokeColor: '#E91E63', strokeWidth: 2 }),
    el('text', { x: 340, y: 295, width: 120, height: 20, text: 'Ingress Controller', fontSize: 14, strokeColor: '#E91E63' }),
    
    // Cluster boundary
    el('rectangle', { x: 50, y: 350, width: 700, height: 250, fillColor: '#F3E5F5', strokeColor: '#9C27B0', strokeWidth: 2 }),
    el('text', { x: 60, y: 360, width: 150, height: 20, text: 'Kubernetes Cluster', fontSize: 14, strokeColor: '#9C27B0' }),
    
    // Services
    el('rectangle', { x: 80, y: 400, width: 180, height: 80, fillColor: '#E8F5E9', strokeColor: '#4CAF50' }),
    el('text', { x: 130, y: 415, width: 80, height: 16, text: 'Service A', fontSize: 12, strokeColor: '#4CAF50' }),
    el('rectangle', { x: 100, y: 445, width: 60, height: 30, fillColor: '#C8E6C9', strokeColor: '#388E3C' }),
    el('text', { x: 115, y: 455, width: 30, height: 12, text: 'Pod', fontSize: 10 }),
    el('rectangle', { x: 180, y: 445, width: 60, height: 30, fillColor: '#C8E6C9', strokeColor: '#388E3C' }),
    el('text', { x: 195, y: 455, width: 30, height: 12, text: 'Pod', fontSize: 10 }),
    
    el('rectangle', { x: 310, y: 400, width: 180, height: 80, fillColor: '#E3F2FD', strokeColor: '#2196F3' }),
    el('text', { x: 360, y: 415, width: 80, height: 16, text: 'Service B', fontSize: 12, strokeColor: '#2196F3' }),
    el('rectangle', { x: 330, y: 445, width: 60, height: 30, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 345, y: 455, width: 30, height: 12, text: 'Pod', fontSize: 10 }),
    el('rectangle', { x: 410, y: 445, width: 60, height: 30, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 425, y: 455, width: 30, height: 12, text: 'Pod', fontSize: 10 }),
    
    el('rectangle', { x: 540, y: 400, width: 180, height: 80, fillColor: '#FFF3E0', strokeColor: '#FF9800' }),
    el('text', { x: 590, y: 415, width: 80, height: 16, text: 'Service C', fontSize: 12, strokeColor: '#FF9800' }),
    el('rectangle', { x: 560, y: 445, width: 60, height: 30, fillColor: '#FFE0B2', strokeColor: '#F57C00' }),
    el('text', { x: 575, y: 455, width: 30, height: 12, text: 'Pod', fontSize: 10 }),
    el('rectangle', { x: 640, y: 445, width: 60, height: 30, fillColor: '#FFE0B2', strokeColor: '#F57C00' }),
    el('text', { x: 655, y: 455, width: 30, height: 12, text: 'Pod', fontSize: 10 }),
    
    // Arrows from Ingress to Services
    el('arrow', { x: 320, y: 330, width: -150, height: 70, strokeColor: '#666' }),
    el('arrow', { x: 400, y: 330, width: 0, height: 70, strokeColor: '#666' }),
    el('arrow', { x: 480, y: 330, width: 150, height: 70, strokeColor: '#666' }),
  ],
};

// ============ CI/CD TEMPLATES ============

const cicdBasicPipeline = {
  id: 'cicd-basic-pipeline',
  name: 'Pipeline CI/CD Basic',
  description: 'Pipeline classique avec Build, Test, Deploy',
  category: 'cicd',
  thumbnail: '/templates/cicd-basic.png',
  elements: [
    // Title
    el('text', { x: 350, y: 30, width: 300, height: 40, text: 'CI/CD Pipeline', fontSize: 28, strokeColor: '#E34F26' }),
    
    // Developer
    el('circle', { x: 50, y: 150, width: 80, height: 80, fillColor: '#E3F2FD', strokeColor: '#2196F3' }),
    el('text', { x: 60, y: 185, width: 60, height: 20, text: 'Developer', fontSize: 11, strokeColor: '#2196F3' }),
    
    // Arrow
    el('arrow', { x: 130, y: 190, width: 50, height: 0, strokeColor: '#666', strokeWidth: 2 }),
    
    // Git Repository
    el('rectangle', { x: 190, y: 140, width: 120, height: 100, fillColor: '#FFEBEE', strokeColor: '#F44336' }),
    el('text', { x: 220, y: 160, width: 60, height: 20, text: 'Git', fontSize: 16, strokeColor: '#F44336' }),
    el('text', { x: 205, y: 200, width: 90, height: 16, text: 'Repository', fontSize: 12 }),
    
    // Arrow
    el('arrow', { x: 310, y: 190, width: 40, height: 0, strokeColor: '#666', strokeWidth: 2 }),
    
    // CI Server
    el('rectangle', { x: 360, y: 100, width: 140, height: 180, fillColor: '#FFF3E0', strokeColor: '#FF9800', strokeWidth: 2 }),
    el('text', { x: 395, y: 115, width: 70, height: 20, text: 'CI Server', fontSize: 14, strokeColor: '#FF9800' }),
    
    // Build stage
    el('rectangle', { x: 375, y: 145, width: 110, height: 35, fillColor: '#E8F5E9', strokeColor: '#4CAF50' }),
    el('text', { x: 410, y: 160, width: 50, height: 14, text: 'Build', fontSize: 12 }),
    
    // Test stage
    el('rectangle', { x: 375, y: 190, width: 110, height: 35, fillColor: '#E3F2FD', strokeColor: '#2196F3' }),
    el('text', { x: 410, y: 205, width: 50, height: 14, text: 'Test', fontSize: 12 }),
    
    // Package stage
    el('rectangle', { x: 375, y: 235, width: 110, height: 35, fillColor: '#F3E5F5', strokeColor: '#9C27B0' }),
    el('text', { x: 400, y: 250, width: 60, height: 14, text: 'Package', fontSize: 12 }),
    
    // Arrow
    el('arrow', { x: 500, y: 190, width: 50, height: 0, strokeColor: '#666', strokeWidth: 2 }),
    
    // Registry
    el('rectangle', { x: 560, y: 150, width: 100, height: 80, fillColor: '#E0F7FA', strokeColor: '#00BCD4' }),
    el('text', { x: 575, y: 175, width: 70, height: 20, text: 'Container', fontSize: 12, strokeColor: '#00BCD4' }),
    el('text', { x: 580, y: 200, width: 60, height: 16, text: 'Registry', fontSize: 11 }),
    
    // Arrow
    el('arrow', { x: 660, y: 190, width: 50, height: 0, strokeColor: '#666', strokeWidth: 2 }),
    
    // CD Server
    el('rectangle', { x: 720, y: 140, width: 100, height: 100, fillColor: '#FCE4EC', strokeColor: '#E91E63' }),
    el('text', { x: 745, y: 165, width: 50, height: 20, text: 'CD', fontSize: 14, strokeColor: '#E91E63' }),
    el('text', { x: 740, y: 200, width: 60, height: 16, text: 'Deploy', fontSize: 12 }),
    
    // Arrow
    el('arrow', { x: 820, y: 190, width: 50, height: 0, strokeColor: '#666', strokeWidth: 2 }),
    
    // Production
    el('rectangle', { x: 880, y: 130, width: 120, height: 120, fillColor: '#E8F5E9', strokeColor: '#4CAF50', strokeWidth: 3 }),
    el('text', { x: 900, y: 175, width: 80, height: 24, text: 'Production', fontSize: 14, strokeColor: '#4CAF50' }),
    el('text', { x: 905, y: 210, width: 70, height: 16, text: 'Environment', fontSize: 10 }),
  ],
};

const cicdGitOps = {
  id: 'cicd-gitops',
  name: 'GitOps Workflow',
  description: 'Architecture GitOps avec ArgoCD/Flux',
  category: 'cicd',
  thumbnail: '/templates/gitops.png',
  elements: [
    // Title
    el('text', { x: 320, y: 20, width: 300, height: 40, text: 'GitOps Workflow', fontSize: 28, strokeColor: '#FF6D00' }),
    
    // App Repository
    el('rectangle', { x: 50, y: 100, width: 150, height: 100, fillColor: '#FFEBEE', strokeColor: '#F44336' }),
    el('text', { x: 80, y: 130, width: 90, height: 20, text: 'App Code', fontSize: 14, strokeColor: '#F44336' }),
    el('text', { x: 70, y: 160, width: 110, height: 16, text: 'Git Repository', fontSize: 11 }),
    
    // Config Repository
    el('rectangle', { x: 50, y: 250, width: 150, height: 100, fillColor: '#E8F5E9', strokeColor: '#4CAF50' }),
    el('text', { x: 75, y: 280, width: 100, height: 20, text: 'Config/Infra', fontSize: 14, strokeColor: '#4CAF50' }),
    el('text', { x: 70, y: 310, width: 110, height: 16, text: 'Git Repository', fontSize: 11 }),
    
    // CI Pipeline
    el('rectangle', { x: 280, y: 100, width: 150, height: 100, fillColor: '#FFF3E0', strokeColor: '#FF9800' }),
    el('text', { x: 310, y: 130, width: 90, height: 20, text: 'CI Pipeline', fontSize: 14, strokeColor: '#FF9800' }),
    el('text', { x: 295, y: 160, width: 120, height: 16, text: 'Build & Push Image', fontSize: 11 }),
    
    // Registry
    el('rectangle', { x: 510, y: 100, width: 120, height: 100, fillColor: '#E0F7FA', strokeColor: '#00BCD4' }),
    el('text', { x: 530, y: 140, width: 80, height: 20, text: 'Container', fontSize: 12, strokeColor: '#00BCD4' }),
    el('text', { x: 540, y: 165, width: 60, height: 16, text: 'Registry', fontSize: 11 }),
    
    // GitOps Operator
    el('rectangle', { x: 280, y: 250, width: 150, height: 100, fillColor: '#FCE4EC', strokeColor: '#E91E63' }),
    el('text', { x: 295, y: 280, width: 120, height: 20, text: 'GitOps Operator', fontSize: 14, strokeColor: '#E91E63' }),
    el('text', { x: 295, y: 310, width: 120, height: 16, text: 'ArgoCD / Flux', fontSize: 11 }),
    
    // Kubernetes Cluster
    el('rectangle', { x: 510, y: 220, width: 250, height: 160, fillColor: '#E3F2FD', strokeColor: '#326CE5', strokeWidth: 3 }),
    el('text', { x: 570, y: 240, width: 130, height: 20, text: 'Kubernetes Cluster', fontSize: 14, strokeColor: '#326CE5' }),
    
    // Pods in cluster
    el('rectangle', { x: 530, y: 280, width: 70, height: 40, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 550, y: 295, width: 30, height: 14, text: 'Pod', fontSize: 11 }),
    el('rectangle', { x: 620, y: 280, width: 70, height: 40, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 640, y: 295, width: 30, height: 14, text: 'Pod', fontSize: 11 }),
    el('rectangle', { x: 530, y: 330, width: 70, height: 40, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 550, y: 345, width: 30, height: 14, text: 'Pod', fontSize: 11 }),
    el('rectangle', { x: 620, y: 330, width: 70, height: 40, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 640, y: 345, width: 30, height: 14, text: 'Pod', fontSize: 11 }),
    
    // Arrows
    el('arrow', { x: 200, y: 150, width: 80, height: 0, strokeColor: '#666', strokeWidth: 2 }),
    el('arrow', { x: 430, y: 150, width: 80, height: 0, strokeColor: '#666', strokeWidth: 2 }),
    el('arrow', { x: 200, y: 300, width: 80, height: 0, strokeColor: '#666', strokeWidth: 2 }),
    el('arrow', { x: 430, y: 300, width: 80, height: 0, strokeColor: '#666', strokeWidth: 2 }),
    el('arrow', { x: 125, y: 200, width: 0, height: 50, strokeColor: '#999', strokeWidth: 1 }),
  ],
};

// ============ AWS TEMPLATES ============

const awsThreeTier = {
  id: 'aws-three-tier',
  name: 'AWS 3-Tier Architecture',
  description: 'Architecture classique AWS avec Web, App et DB tiers',
  category: 'aws',
  thumbnail: '/templates/aws-3tier.png',
  elements: [
    // Title
    el('text', { x: 350, y: 20, width: 300, height: 40, text: 'AWS 3-Tier Architecture', fontSize: 26, strokeColor: '#FF9900' }),
    
    // VPC Boundary
    el('rectangle', { x: 50, y: 80, width: 900, height: 450, fillColor: '#FFF8E1', strokeColor: '#FF9900', strokeWidth: 3 }),
    el('text', { x: 70, y: 95, width: 60, height: 20, text: 'VPC', fontSize: 14, strokeColor: '#FF9900' }),
    
    // Internet Gateway
    el('rectangle', { x: 440, y: 60, width: 120, height: 50, fillColor: '#E3F2FD', strokeColor: '#2196F3' }),
    el('text', { x: 450, y: 80, width: 100, height: 16, text: 'Internet Gateway', fontSize: 11, strokeColor: '#2196F3' }),
    
    // Public Subnet - Web Tier
    el('rectangle', { x: 80, y: 130, width: 840, height: 120, fillColor: '#E8F5E9', strokeColor: '#4CAF50', strokeWidth: 2 }),
    el('text', { x: 90, y: 140, width: 120, height: 18, text: 'Public Subnet (Web Tier)', fontSize: 12, strokeColor: '#4CAF50' }),
    
    // ALB
    el('rectangle', { x: 200, y: 165, width: 140, height: 60, fillColor: '#C8E6C9', strokeColor: '#388E3C' }),
    el('text', { x: 215, y: 190, width: 110, height: 16, text: 'Application LB', fontSize: 11 }),
    
    // Web Servers
    el('rectangle', { x: 450, y: 165, width: 100, height: 60, fillColor: '#DCEDC8', strokeColor: '#689F38' }),
    el('text', { x: 470, y: 190, width: 60, height: 14, text: 'Web EC2', fontSize: 11 }),
    el('rectangle', { x: 580, y: 165, width: 100, height: 60, fillColor: '#DCEDC8', strokeColor: '#689F38' }),
    el('text', { x: 600, y: 190, width: 60, height: 14, text: 'Web EC2', fontSize: 11 }),
    el('rectangle', { x: 710, y: 165, width: 100, height: 60, fillColor: '#DCEDC8', strokeColor: '#689F38' }),
    el('text', { x: 730, y: 190, width: 60, height: 14, text: 'Web EC2', fontSize: 11 }),
    
    // Private Subnet - App Tier
    el('rectangle', { x: 80, y: 270, width: 840, height: 120, fillColor: '#E3F2FD', strokeColor: '#2196F3', strokeWidth: 2 }),
    el('text', { x: 90, y: 280, width: 140, height: 18, text: 'Private Subnet (App Tier)', fontSize: 12, strokeColor: '#2196F3' }),
    
    // Internal ALB
    el('rectangle', { x: 200, y: 305, width: 140, height: 60, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 220, y: 330, width: 100, height: 16, text: 'Internal ALB', fontSize: 11 }),
    
    // App Servers
    el('rectangle', { x: 450, y: 305, width: 100, height: 60, fillColor: '#90CAF9', strokeColor: '#1565C0' }),
    el('text', { x: 470, y: 330, width: 60, height: 14, text: 'App EC2', fontSize: 11 }),
    el('rectangle', { x: 580, y: 305, width: 100, height: 60, fillColor: '#90CAF9', strokeColor: '#1565C0' }),
    el('text', { x: 600, y: 330, width: 60, height: 14, text: 'App EC2', fontSize: 11 }),
    el('rectangle', { x: 710, y: 305, width: 100, height: 60, fillColor: '#90CAF9', strokeColor: '#1565C0' }),
    el('text', { x: 730, y: 330, width: 60, height: 14, text: 'App EC2', fontSize: 11 }),
    
    // Private Subnet - DB Tier
    el('rectangle', { x: 80, y: 410, width: 840, height: 100, fillColor: '#FCE4EC', strokeColor: '#E91E63', strokeWidth: 2 }),
    el('text', { x: 90, y: 420, width: 140, height: 18, text: 'Private Subnet (DB Tier)', fontSize: 12, strokeColor: '#E91E63' }),
    
    // RDS
    el('rectangle', { x: 300, y: 445, width: 140, height: 50, fillColor: '#F8BBD9', strokeColor: '#C2185B' }),
    el('text', { x: 330, y: 465, width: 80, height: 14, text: 'RDS Primary', fontSize: 11 }),
    el('rectangle', { x: 560, y: 445, width: 140, height: 50, fillColor: '#F8BBD9', strokeColor: '#C2185B' }),
    el('text', { x: 585, y: 465, width: 90, height: 14, text: 'RDS Standby', fontSize: 11 }),
    
    // Arrows
    el('arrow', { x: 500, y: 110, width: 0, height: 55, strokeColor: '#666' }),
    el('arrow', { x: 340, y: 195, width: 110, height: 0, strokeColor: '#666' }),
    el('arrow', { x: 340, y: 335, width: 110, height: 0, strokeColor: '#666' }),
    el('arrow', { x: 500, y: 250, width: 0, height: 55, strokeColor: '#666' }),
    el('arrow', { x: 500, y: 390, width: 0, height: 55, strokeColor: '#666' }),
    el('arrow', { x: 440, y: 470, width: 120, height: 0, strokeColor: '#999', strokeWidth: 1 }),
  ],
};

const awsServerless = {
  id: 'aws-serverless',
  name: 'AWS Serverless',
  description: 'Architecture Serverless avec Lambda, API Gateway, DynamoDB',
  category: 'aws',
  thumbnail: '/templates/aws-serverless.png',
  elements: [
    // Title
    el('text', { x: 320, y: 20, width: 300, height: 40, text: 'AWS Serverless Architecture', fontSize: 24, strokeColor: '#FF9900' }),
    
    // Client
    el('rectangle', { x: 50, y: 180, width: 100, height: 80, fillColor: '#E3F2FD', strokeColor: '#2196F3' }),
    el('text', { x: 75, y: 210, width: 50, height: 20, text: 'Client', fontSize: 14 }),
    el('text', { x: 60, y: 235, width: 80, height: 14, text: 'Web/Mobile', fontSize: 10 }),
    
    // CloudFront
    el('rectangle', { x: 200, y: 180, width: 120, height: 80, fillColor: '#FFF3E0', strokeColor: '#FF9800' }),
    el('text', { x: 215, y: 210, width: 90, height: 20, text: 'CloudFront', fontSize: 13, strokeColor: '#FF9800' }),
    el('text', { x: 230, y: 235, width: 60, height: 14, text: 'CDN', fontSize: 10 }),
    
    // API Gateway
    el('rectangle', { x: 380, y: 180, width: 120, height: 80, fillColor: '#FCE4EC', strokeColor: '#E91E63' }),
    el('text', { x: 395, y: 205, width: 90, height: 20, text: 'API Gateway', fontSize: 13, strokeColor: '#E91E63' }),
    el('text', { x: 400, y: 235, width: 80, height: 14, text: 'REST API', fontSize: 10 }),
    
    // Lambda Functions
    el('rectangle', { x: 560, y: 100, width: 140, height: 240, fillColor: '#FFF8E1', strokeColor: '#FFB300', strokeWidth: 2 }),
    el('text', { x: 580, y: 115, width: 100, height: 18, text: 'Lambda Functions', fontSize: 12, strokeColor: '#FFB300' }),
    
    el('rectangle', { x: 575, y: 145, width: 110, height: 45, fillColor: '#FFECB3', strokeColor: '#FFA000' }),
    el('text', { x: 590, y: 163, width: 80, height: 14, text: 'Auth Lambda', fontSize: 10 }),
    
    el('rectangle', { x: 575, y: 200, width: 110, height: 45, fillColor: '#FFECB3', strokeColor: '#FFA000' }),
    el('text', { x: 590, y: 218, width: 80, height: 14, text: 'API Lambda', fontSize: 10 }),
    
    el('rectangle', { x: 575, y: 255, width: 110, height: 45, fillColor: '#FFECB3', strokeColor: '#FFA000' }),
    el('text', { x: 585, y: 273, width: 90, height: 14, text: 'Process Lambda', fontSize: 10 }),
    
    // DynamoDB
    el('rectangle', { x: 760, y: 150, width: 130, height: 70, fillColor: '#E3F2FD', strokeColor: '#2196F3' }),
    el('text', { x: 785, y: 180, width: 80, height: 18, text: 'DynamoDB', fontSize: 13, strokeColor: '#2196F3' }),
    
    // S3
    el('rectangle', { x: 760, y: 250, width: 130, height: 70, fillColor: '#E8F5E9', strokeColor: '#4CAF50' }),
    el('text', { x: 805, y: 280, width: 40, height: 18, text: 'S3', fontSize: 14, strokeColor: '#4CAF50' }),
    
    // Cognito
    el('rectangle', { x: 380, y: 320, width: 120, height: 60, fillColor: '#F3E5F5', strokeColor: '#9C27B0' }),
    el('text', { x: 405, y: 345, width: 70, height: 18, text: 'Cognito', fontSize: 13, strokeColor: '#9C27B0' }),
    
    // Arrows
    el('arrow', { x: 150, y: 220, width: 50, height: 0, strokeColor: '#666' }),
    el('arrow', { x: 320, y: 220, width: 60, height: 0, strokeColor: '#666' }),
    el('arrow', { x: 500, y: 220, width: 60, height: 0, strokeColor: '#666' }),
    el('arrow', { x: 700, y: 185, width: 60, height: 0, strokeColor: '#666' }),
    el('arrow', { x: 700, y: 280, width: 60, height: 0, strokeColor: '#666' }),
    el('arrow', { x: 440, y: 260, width: 0, height: 60, strokeColor: '#666' }),
  ],
};

// ============ GCP TEMPLATES ============

const gcpGKE = {
  id: 'gcp-gke',
  name: 'GCP GKE Architecture',
  description: 'Architecture Google Kubernetes Engine complète',
  category: 'gcp',
  thumbnail: '/templates/gcp-gke.png',
  elements: [
    // Title
    el('text', { x: 350, y: 20, width: 300, height: 40, text: 'Google Cloud GKE', fontSize: 26, strokeColor: '#4285F4' }),
    
    // VPC
    el('rectangle', { x: 50, y: 80, width: 850, height: 420, fillColor: '#E8F0FE', strokeColor: '#4285F4', strokeWidth: 2 }),
    el('text', { x: 70, y: 95, width: 100, height: 18, text: 'VPC Network', fontSize: 12, strokeColor: '#4285F4' }),
    
    // Cloud Load Balancing
    el('rectangle', { x: 380, y: 60, width: 180, height: 50, fillColor: '#FCE4EC', strokeColor: '#DB4437' }),
    el('text', { x: 400, y: 80, width: 140, height: 16, text: 'Cloud Load Balancing', fontSize: 12, strokeColor: '#DB4437' }),
    
    // GKE Cluster
    el('rectangle', { x: 100, y: 140, width: 750, height: 280, fillColor: '#E3F2FD', strokeColor: '#326CE5', strokeWidth: 3 }),
    el('text', { x: 120, y: 155, width: 100, height: 20, text: 'GKE Cluster', fontSize: 14, strokeColor: '#326CE5' }),
    
    // Node Pool 1
    el('rectangle', { x: 130, y: 190, width: 220, height: 200, fillColor: '#F1F8E9', strokeColor: '#689F38', strokeWidth: 2 }),
    el('text', { x: 150, y: 205, width: 100, height: 16, text: 'Node Pool 1', fontSize: 12, strokeColor: '#689F38' }),
    
    el('rectangle', { x: 150, y: 235, width: 80, height: 50, fillColor: '#DCEDC8', strokeColor: '#558B2F' }),
    el('text', { x: 170, y: 255, width: 40, height: 14, text: 'Pod', fontSize: 11 }),
    el('rectangle', { x: 250, y: 235, width: 80, height: 50, fillColor: '#DCEDC8', strokeColor: '#558B2F' }),
    el('text', { x: 270, y: 255, width: 40, height: 14, text: 'Pod', fontSize: 11 }),
    el('rectangle', { x: 150, y: 300, width: 80, height: 50, fillColor: '#DCEDC8', strokeColor: '#558B2F' }),
    el('text', { x: 170, y: 320, width: 40, height: 14, text: 'Pod', fontSize: 11 }),
    el('rectangle', { x: 250, y: 300, width: 80, height: 50, fillColor: '#DCEDC8', strokeColor: '#558B2F' }),
    el('text', { x: 270, y: 320, width: 40, height: 14, text: 'Pod', fontSize: 11 }),
    
    // Node Pool 2
    el('rectangle', { x: 380, y: 190, width: 220, height: 200, fillColor: '#E3F2FD', strokeColor: '#1976D2', strokeWidth: 2 }),
    el('text', { x: 400, y: 205, width: 100, height: 16, text: 'Node Pool 2', fontSize: 12, strokeColor: '#1976D2' }),
    
    el('rectangle', { x: 400, y: 235, width: 80, height: 50, fillColor: '#BBDEFB', strokeColor: '#1565C0' }),
    el('text', { x: 420, y: 255, width: 40, height: 14, text: 'Pod', fontSize: 11 }),
    el('rectangle', { x: 500, y: 235, width: 80, height: 50, fillColor: '#BBDEFB', strokeColor: '#1565C0' }),
    el('text', { x: 520, y: 255, width: 40, height: 14, text: 'Pod', fontSize: 11 }),
    el('rectangle', { x: 400, y: 300, width: 80, height: 50, fillColor: '#BBDEFB', strokeColor: '#1565C0' }),
    el('text', { x: 420, y: 320, width: 40, height: 14, text: 'Pod', fontSize: 11 }),
    el('rectangle', { x: 500, y: 300, width: 80, height: 50, fillColor: '#BBDEFB', strokeColor: '#1565C0' }),
    el('text', { x: 520, y: 320, width: 40, height: 14, text: 'Pod', fontSize: 11 }),
    
    // Services
    el('rectangle', { x: 630, y: 190, width: 200, height: 200, fillColor: '#FFF8E1', strokeColor: '#F9A825', strokeWidth: 2 }),
    el('text', { x: 700, y: 205, width: 60, height: 16, text: 'Services', fontSize: 12, strokeColor: '#F9A825' }),
    
    el('rectangle', { x: 650, y: 240, width: 160, height: 40, fillColor: '#FFECB3', strokeColor: '#FFA000' }),
    el('text', { x: 680, y: 255, width: 100, height: 14, text: 'Cloud SQL', fontSize: 11 }),
    el('rectangle', { x: 650, y: 295, width: 160, height: 40, fillColor: '#FFECB3', strokeColor: '#FFA000' }),
    el('text', { x: 670, y: 310, width: 120, height: 14, text: 'Cloud Storage', fontSize: 11 }),
    el('rectangle', { x: 650, y: 350, width: 160, height: 35, fillColor: '#FFECB3', strokeColor: '#FFA000' }),
    el('text', { x: 680, y: 363, width: 100, height: 14, text: 'Pub/Sub', fontSize: 11 }),
    
    // Cloud Armor
    el('rectangle', { x: 380, y: 445, width: 180, height: 45, fillColor: '#FFEBEE', strokeColor: '#D32F2F' }),
    el('text', { x: 420, y: 463, width: 100, height: 14, text: 'Cloud Armor', fontSize: 12, strokeColor: '#D32F2F' }),
    
    // Arrows
    el('arrow', { x: 470, y: 110, width: 0, height: 30, strokeColor: '#666' }),
  ],
};

// ============ AZURE TEMPLATES ============

const azureAKS = {
  id: 'azure-aks',
  name: 'Azure AKS Architecture',
  description: 'Architecture Azure Kubernetes Service',
  category: 'azure',
  thumbnail: '/templates/azure-aks.png',
  elements: [
    // Title
    el('text', { x: 350, y: 20, width: 300, height: 40, text: 'Azure AKS Architecture', fontSize: 26, strokeColor: '#0089D6' }),
    
    // Resource Group
    el('rectangle', { x: 50, y: 80, width: 850, height: 420, fillColor: '#E3F2FD', strokeColor: '#0089D6', strokeWidth: 2 }),
    el('text', { x: 70, y: 95, width: 120, height: 18, text: 'Resource Group', fontSize: 12, strokeColor: '#0089D6' }),
    
    // Azure Front Door
    el('rectangle', { x: 380, y: 60, width: 180, height: 50, fillColor: '#FCE4EC', strokeColor: '#E91E63' }),
    el('text', { x: 410, y: 80, width: 120, height: 16, text: 'Azure Front Door', fontSize: 12, strokeColor: '#E91E63' }),
    
    // VNET
    el('rectangle', { x: 80, y: 130, width: 800, height: 350, fillColor: '#F3E5F5', strokeColor: '#7B1FA2', strokeWidth: 2 }),
    el('text', { x: 100, y: 145, width: 100, height: 18, text: 'Virtual Network', fontSize: 12, strokeColor: '#7B1FA2' }),
    
    // AKS Cluster
    el('rectangle', { x: 120, y: 180, width: 500, height: 280, fillColor: '#E8F5E9', strokeColor: '#388E3C', strokeWidth: 2 }),
    el('text', { x: 140, y: 195, width: 100, height: 18, text: 'AKS Cluster', fontSize: 14, strokeColor: '#388E3C' }),
    
    // System Node Pool
    el('rectangle', { x: 140, y: 230, width: 220, height: 100, fillColor: '#C8E6C9', strokeColor: '#2E7D32' }),
    el('text', { x: 170, y: 245, width: 160, height: 16, text: 'System Node Pool', fontSize: 11, strokeColor: '#2E7D32' }),
    el('rectangle', { x: 160, y: 275, width: 60, height: 40, fillColor: '#A5D6A7', strokeColor: '#1B5E20' }),
    el('text', { x: 175, y: 290, width: 30, height: 12, text: 'Pod', fontSize: 10 }),
    el('rectangle', { x: 240, y: 275, width: 60, height: 40, fillColor: '#A5D6A7', strokeColor: '#1B5E20' }),
    el('text', { x: 255, y: 290, width: 30, height: 12, text: 'Pod', fontSize: 10 }),
    
    // User Node Pool
    el('rectangle', { x: 140, y: 345, width: 220, height: 100, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 170, y: 360, width: 160, height: 16, text: 'User Node Pool', fontSize: 11, strokeColor: '#1976D2' }),
    el('rectangle', { x: 160, y: 390, width: 60, height: 40, fillColor: '#90CAF9', strokeColor: '#1565C0' }),
    el('text', { x: 175, y: 405, width: 30, height: 12, text: 'Pod', fontSize: 10 }),
    el('rectangle', { x: 240, y: 390, width: 60, height: 40, fillColor: '#90CAF9', strokeColor: '#1565C0' }),
    el('text', { x: 255, y: 405, width: 30, height: 12, text: 'Pod', fontSize: 10 }),
    
    // Ingress Controller
    el('rectangle', { x: 380, y: 230, width: 220, height: 80, fillColor: '#FFF3E0', strokeColor: '#F57C00' }),
    el('text', { x: 420, y: 265, width: 140, height: 16, text: 'Ingress Controller', fontSize: 12, strokeColor: '#F57C00' }),
    
    // Azure Services
    el('rectangle', { x: 650, y: 180, width: 200, height: 280, fillColor: '#FFF8E1', strokeColor: '#FFA000', strokeWidth: 2 }),
    el('text', { x: 700, y: 195, width: 100, height: 18, text: 'Azure Services', fontSize: 12, strokeColor: '#FFA000' }),
    
    el('rectangle', { x: 670, y: 230, width: 160, height: 45, fillColor: '#FFECB3', strokeColor: '#FF8F00' }),
    el('text', { x: 695, y: 248, width: 110, height: 14, text: 'Azure SQL DB', fontSize: 11 }),
    
    el('rectangle', { x: 670, y: 290, width: 160, height: 45, fillColor: '#FFECB3', strokeColor: '#FF8F00' }),
    el('text', { x: 690, y: 308, width: 120, height: 14, text: 'Blob Storage', fontSize: 11 }),
    
    el('rectangle', { x: 670, y: 350, width: 160, height: 45, fillColor: '#FFECB3', strokeColor: '#FF8F00' }),
    el('text', { x: 695, y: 368, width: 110, height: 14, text: 'Key Vault', fontSize: 11 }),
    
    el('rectangle', { x: 670, y: 410, width: 160, height: 40, fillColor: '#FFECB3', strokeColor: '#FF8F00' }),
    el('text', { x: 690, y: 425, width: 120, height: 14, text: 'Container Registry', fontSize: 10 }),
    
    // Arrows
    el('arrow', { x: 470, y: 110, width: 0, height: 20, strokeColor: '#666' }),
    el('arrow', { x: 620, y: 270, width: 50, height: 0, strokeColor: '#666' }),
  ],
};

// ============ DEVOPS GENERAL TEMPLATES ============

const devopsMonitoring = {
  id: 'devops-monitoring',
  name: 'Monitoring Stack',
  description: 'Stack de monitoring avec Prometheus, Grafana, AlertManager',
  category: 'monitoring',
  thumbnail: '/templates/monitoring.png',
  elements: [
    // Title
    el('text', { x: 320, y: 20, width: 300, height: 40, text: 'Monitoring Stack', fontSize: 28, strokeColor: '#E6522C' }),
    
    // Applications
    el('rectangle', { x: 50, y: 100, width: 200, height: 300, fillColor: '#E3F2FD', strokeColor: '#2196F3', strokeWidth: 2 }),
    el('text', { x: 100, y: 115, width: 100, height: 18, text: 'Applications', fontSize: 14, strokeColor: '#2196F3' }),
    
    el('rectangle', { x: 70, y: 150, width: 160, height: 50, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 110, y: 170, width: 80, height: 14, text: 'Service A', fontSize: 11 }),
    
    el('rectangle', { x: 70, y: 220, width: 160, height: 50, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 110, y: 240, width: 80, height: 14, text: 'Service B', fontSize: 11 }),
    
    el('rectangle', { x: 70, y: 290, width: 160, height: 50, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 110, y: 310, width: 80, height: 14, text: 'Service C', fontSize: 11 }),
    
    // Node Exporter
    el('rectangle', { x: 70, y: 360, width: 160, height: 30, fillColor: '#E8F5E9', strokeColor: '#4CAF50' }),
    el('text', { x: 95, y: 370, width: 110, height: 12, text: 'Node Exporter', fontSize: 10 }),
    
    // Prometheus
    el('rectangle', { x: 320, y: 150, width: 200, height: 150, fillColor: '#FFF3E0', strokeColor: '#E6522C', strokeWidth: 3 }),
    el('text', { x: 370, y: 175, width: 100, height: 24, text: 'Prometheus', fontSize: 18, strokeColor: '#E6522C' }),
    el('text', { x: 350, y: 220, width: 140, height: 16, text: 'Metrics Collection', fontSize: 11 }),
    el('text', { x: 350, y: 250, width: 140, height: 16, text: '& Storage (TSDB)', fontSize: 11 }),
    
    // AlertManager
    el('rectangle', { x: 320, y: 330, width: 200, height: 80, fillColor: '#FCE4EC', strokeColor: '#E91E63' }),
    el('text', { x: 365, y: 360, width: 110, height: 18, text: 'AlertManager', fontSize: 14, strokeColor: '#E91E63' }),
    el('text', { x: 360, y: 390, width: 120, height: 14, text: 'Alert Routing', fontSize: 10 }),
    
    // Grafana
    el('rectangle', { x: 600, y: 150, width: 200, height: 150, fillColor: '#FFF8E1', strokeColor: '#F9A825', strokeWidth: 3 }),
    el('text', { x: 665, y: 175, width: 70, height: 24, text: 'Grafana', fontSize: 18, strokeColor: '#F9A825' }),
    el('text', { x: 630, y: 220, width: 140, height: 16, text: 'Visualization &', fontSize: 11 }),
    el('text', { x: 640, y: 250, width: 120, height: 16, text: 'Dashboards', fontSize: 11 }),
    
    // Notifications
    el('rectangle', { x: 600, y: 330, width: 200, height: 80, fillColor: '#E8F5E9', strokeColor: '#4CAF50' }),
    el('text', { x: 650, y: 360, width: 100, height: 18, text: 'Notifications', fontSize: 14, strokeColor: '#4CAF50' }),
    el('text', { x: 620, y: 390, width: 160, height: 14, text: 'Slack / Email / PagerDuty', fontSize: 9 }),
    
    // Arrows
    el('arrow', { x: 250, y: 200, width: 70, height: 0, strokeColor: '#E6522C', strokeWidth: 2 }),
    el('arrow', { x: 520, y: 225, width: 80, height: 0, strokeColor: '#F9A825', strokeWidth: 2 }),
    el('arrow', { x: 420, y: 300, width: 0, height: 30, strokeColor: '#E91E63', strokeWidth: 2 }),
    el('arrow', { x: 520, y: 370, width: 80, height: 0, strokeColor: '#4CAF50', strokeWidth: 2 }),
  ],
};

const devopsMicroservices = {
  id: 'devops-microservices',
  name: 'Microservices Architecture',
  description: 'Architecture microservices avec API Gateway et Service Mesh',
  category: 'devops',
  thumbnail: '/templates/microservices.png',
  elements: [
    // Title
    el('text', { x: 350, y: 20, width: 300, height: 40, text: 'Microservices Architecture', fontSize: 24, strokeColor: '#00ADD8' }),
    
    // Clients
    el('rectangle', { x: 50, y: 130, width: 120, height: 200, fillColor: '#E3F2FD', strokeColor: '#2196F3' }),
    el('text', { x: 80, y: 145, width: 60, height: 18, text: 'Clients', fontSize: 14, strokeColor: '#2196F3' }),
    el('rectangle', { x: 70, y: 175, width: 80, height: 40, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 95, y: 190, width: 30, height: 12, text: 'Web', fontSize: 10 }),
    el('rectangle', { x: 70, y: 230, width: 80, height: 40, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 85, y: 245, width: 50, height: 12, text: 'Mobile', fontSize: 10 }),
    el('rectangle', { x: 70, y: 285, width: 80, height: 40, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 95, y: 300, width: 30, height: 12, text: 'IoT', fontSize: 10 }),
    
    // API Gateway
    el('rectangle', { x: 220, y: 150, width: 100, height: 160, fillColor: '#FCE4EC', strokeColor: '#E91E63', strokeWidth: 2 }),
    el('text', { x: 235, y: 175, width: 70, height: 18, text: 'API', fontSize: 14, strokeColor: '#E91E63' }),
    el('text', { x: 230, y: 200, width: 80, height: 16, text: 'Gateway', fontSize: 12, strokeColor: '#E91E63' }),
    el('text', { x: 230, y: 240, width: 80, height: 12, text: '• Auth', fontSize: 9 }),
    el('text', { x: 230, y: 260, width: 80, height: 12, text: '• Rate Limit', fontSize: 9 }),
    el('text', { x: 230, y: 280, width: 80, height: 12, text: '• Routing', fontSize: 9 }),
    
    // Service Mesh
    el('rectangle', { x: 370, y: 100, width: 450, height: 320, fillColor: '#F3E5F5', strokeColor: '#7B42BC', strokeWidth: 2 }),
    el('text', { x: 520, y: 115, width: 150, height: 18, text: 'Service Mesh (Istio)', fontSize: 14, strokeColor: '#7B42BC' }),
    
    // Microservices
    el('rectangle', { x: 400, y: 150, width: 120, height: 80, fillColor: '#E8F5E9', strokeColor: '#4CAF50' }),
    el('text', { x: 425, y: 175, width: 70, height: 14, text: 'User Svc', fontSize: 11, strokeColor: '#4CAF50' }),
    el('rectangle', { x: 420, y: 200, width: 80, height: 20, fillColor: '#C8E6C9', strokeColor: '#388E3C' }),
    el('text', { x: 445, y: 206, width: 30, height: 10, text: 'Pod', fontSize: 8 }),
    
    el('rectangle', { x: 550, y: 150, width: 120, height: 80, fillColor: '#E3F2FD', strokeColor: '#2196F3' }),
    el('text', { x: 570, y: 175, width: 80, height: 14, text: 'Order Svc', fontSize: 11, strokeColor: '#2196F3' }),
    el('rectangle', { x: 570, y: 200, width: 80, height: 20, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 595, y: 206, width: 30, height: 10, text: 'Pod', fontSize: 8 }),
    
    el('rectangle', { x: 700, y: 150, width: 100, height: 80, fillColor: '#FFF3E0', strokeColor: '#FF9800' }),
    el('text', { x: 715, y: 175, width: 70, height: 14, text: 'Payment', fontSize: 11, strokeColor: '#FF9800' }),
    el('rectangle', { x: 720, y: 200, width: 60, height: 20, fillColor: '#FFE0B2', strokeColor: '#F57C00' }),
    el('text', { x: 740, y: 206, width: 20, height: 10, text: 'Pod', fontSize: 8 }),
    
    el('rectangle', { x: 400, y: 260, width: 120, height: 80, fillColor: '#FFEBEE', strokeColor: '#F44336' }),
    el('text', { x: 420, y: 285, width: 80, height: 14, text: 'Inventory', fontSize: 11, strokeColor: '#F44336' }),
    el('rectangle', { x: 420, y: 310, width: 80, height: 20, fillColor: '#FFCDD2', strokeColor: '#D32F2F' }),
    el('text', { x: 445, y: 316, width: 30, height: 10, text: 'Pod', fontSize: 8 }),
    
    el('rectangle', { x: 550, y: 260, width: 120, height: 80, fillColor: '#E0F7FA', strokeColor: '#00BCD4' }),
    el('text', { x: 570, y: 285, width: 80, height: 14, text: 'Shipping', fontSize: 11, strokeColor: '#00BCD4' }),
    el('rectangle', { x: 570, y: 310, width: 80, height: 20, fillColor: '#B2EBF2', strokeColor: '#0097A7' }),
    el('text', { x: 595, y: 316, width: 30, height: 10, text: 'Pod', fontSize: 8 }),
    
    el('rectangle', { x: 700, y: 260, width: 100, height: 80, fillColor: '#F3E5F5', strokeColor: '#9C27B0' }),
    el('text', { x: 715, y: 285, width: 70, height: 14, text: 'Notif Svc', fontSize: 11, strokeColor: '#9C27B0' }),
    el('rectangle', { x: 720, y: 310, width: 60, height: 20, fillColor: '#E1BEE7', strokeColor: '#7B1FA2' }),
    el('text', { x: 740, y: 316, width: 20, height: 10, text: 'Pod', fontSize: 8 }),
    
    // Message Queue
    el('rectangle', { x: 480, y: 370, width: 200, height: 40, fillColor: '#FFF8E1', strokeColor: '#FFA000' }),
    el('text', { x: 520, y: 385, width: 120, height: 14, text: 'Message Queue (Kafka)', fontSize: 10, strokeColor: '#FFA000' }),
    
    // Arrows
    el('arrow', { x: 170, y: 230, width: 50, height: 0, strokeColor: '#666' }),
    el('arrow', { x: 320, y: 230, width: 50, height: 0, strokeColor: '#666' }),
  ],
};

// ============ NETWORKING TEMPLATES ============

const networkingVPC = {
  id: 'networking-vpc',
  name: 'VPC Network Design',
  description: 'Design de réseau VPC avec subnets publics et privés',
  category: 'networking',
  thumbnail: '/templates/vpc.png',
  elements: [
    // Title
    el('text', { x: 350, y: 20, width: 300, height: 40, text: 'VPC Network Design', fontSize: 26, strokeColor: '#7B42BC' }),
    
    // Internet
    el('circle', { x: 400, y: 60, width: 100, height: 50, fillColor: '#E3F2FD', strokeColor: '#2196F3' }),
    el('text', { x: 425, y: 80, width: 50, height: 16, text: 'Internet', fontSize: 11 }),
    
    // VPC
    el('rectangle', { x: 50, y: 130, width: 800, height: 380, fillColor: '#F3E5F5', strokeColor: '#7B42BC', strokeWidth: 3 }),
    el('text', { x: 70, y: 145, width: 200, height: 20, text: 'VPC (10.0.0.0/16)', fontSize: 14, strokeColor: '#7B42BC' }),
    
    // Internet Gateway
    el('rectangle', { x: 400, y: 115, width: 100, height: 40, fillColor: '#E8F5E9', strokeColor: '#4CAF50' }),
    el('text', { x: 430, y: 130, width: 40, height: 14, text: 'IGW', fontSize: 11 }),
    
    // Public Subnet AZ-A
    el('rectangle', { x: 80, y: 180, width: 350, height: 140, fillColor: '#E8F5E9', strokeColor: '#4CAF50', strokeWidth: 2 }),
    el('text', { x: 90, y: 195, width: 200, height: 16, text: 'Public Subnet AZ-A (10.0.1.0/24)', fontSize: 11, strokeColor: '#4CAF50' }),
    
    el('rectangle', { x: 100, y: 230, width: 80, height: 60, fillColor: '#C8E6C9', strokeColor: '#388E3C' }),
    el('text', { x: 120, y: 255, width: 40, height: 14, text: 'NAT', fontSize: 10 }),
    
    el('rectangle', { x: 200, y: 230, width: 100, height: 60, fillColor: '#DCEDC8', strokeColor: '#689F38' }),
    el('text', { x: 215, y: 255, width: 70, height: 14, text: 'Bastion', fontSize: 10 }),
    
    el('rectangle', { x: 320, y: 230, width: 100, height: 60, fillColor: '#DCEDC8', strokeColor: '#689F38' }),
    el('text', { x: 350, y: 255, width: 40, height: 14, text: 'ALB', fontSize: 10 }),
    
    // Public Subnet AZ-B
    el('rectangle', { x: 470, y: 180, width: 350, height: 140, fillColor: '#E8F5E9', strokeColor: '#4CAF50', strokeWidth: 2 }),
    el('text', { x: 480, y: 195, width: 200, height: 16, text: 'Public Subnet AZ-B (10.0.2.0/24)', fontSize: 11, strokeColor: '#4CAF50' }),
    
    el('rectangle', { x: 490, y: 230, width: 80, height: 60, fillColor: '#C8E6C9', strokeColor: '#388E3C' }),
    el('text', { x: 510, y: 255, width: 40, height: 14, text: 'NAT', fontSize: 10 }),
    
    el('rectangle', { x: 600, y: 230, width: 100, height: 60, fillColor: '#DCEDC8', strokeColor: '#689F38' }),
    el('text', { x: 630, y: 255, width: 40, height: 14, text: 'ALB', fontSize: 10 }),
    
    // Private Subnet AZ-A
    el('rectangle', { x: 80, y: 350, width: 350, height: 140, fillColor: '#E3F2FD', strokeColor: '#2196F3', strokeWidth: 2 }),
    el('text', { x: 90, y: 365, width: 200, height: 16, text: 'Private Subnet AZ-A (10.0.3.0/24)', fontSize: 11, strokeColor: '#2196F3' }),
    
    el('rectangle', { x: 100, y: 400, width: 100, height: 60, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 120, y: 425, width: 60, height: 14, text: 'App EC2', fontSize: 10 }),
    
    el('rectangle', { x: 220, y: 400, width: 100, height: 60, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 240, y: 425, width: 60, height: 14, text: 'App EC2', fontSize: 10 }),
    
    el('rectangle', { x: 340, y: 400, width: 80, height: 60, fillColor: '#90CAF9', strokeColor: '#1565C0' }),
    el('text', { x: 365, y: 425, width: 30, height: 14, text: 'RDS', fontSize: 10 }),
    
    // Private Subnet AZ-B
    el('rectangle', { x: 470, y: 350, width: 350, height: 140, fillColor: '#E3F2FD', strokeColor: '#2196F3', strokeWidth: 2 }),
    el('text', { x: 480, y: 365, width: 200, height: 16, text: 'Private Subnet AZ-B (10.0.4.0/24)', fontSize: 11, strokeColor: '#2196F3' }),
    
    el('rectangle', { x: 490, y: 400, width: 100, height: 60, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 510, y: 425, width: 60, height: 14, text: 'App EC2', fontSize: 10 }),
    
    el('rectangle', { x: 610, y: 400, width: 100, height: 60, fillColor: '#BBDEFB', strokeColor: '#1976D2' }),
    el('text', { x: 630, y: 425, width: 60, height: 14, text: 'App EC2', fontSize: 10 }),
    
    el('rectangle', { x: 730, y: 400, width: 80, height: 60, fillColor: '#90CAF9', strokeColor: '#1565C0' }),
    el('text', { x: 750, y: 425, width: 40, height: 14, text: 'RDS', fontSize: 10 }),
    
    // Route arrows
    el('arrow', { x: 450, y: 110, width: 0, height: -25, strokeColor: '#666' }),
    el('arrow', { x: 140, y: 320, width: 0, height: 30, strokeColor: '#388E3C' }),
    el('arrow', { x: 530, y: 320, width: 0, height: 30, strokeColor: '#388E3C' }),
  ],
};

// Export all templates
export const DRAW_TEMPLATES = [
  // Kubernetes
  k8sBasicCluster,
  k8sWithIngress,
  
  // CI/CD
  cicdBasicPipeline,
  cicdGitOps,
  
  // AWS
  awsThreeTier,
  awsServerless,
  
  // GCP
  gcpGKE,
  
  // Azure
  azureAKS,
  
  // DevOps
  devopsMonitoring,
  devopsMicroservices,
  
  // Networking
  networkingVPC,
];

// Helper functions
export const getTemplatesByCategory = (category) => {
  if (category === 'all') return DRAW_TEMPLATES;
  return DRAW_TEMPLATES.filter(t => t.category === category);
};

export const searchTemplates = (query) => {
  const lowerQuery = query.toLowerCase();
  return DRAW_TEMPLATES.filter(t => 
    t.name.toLowerCase().includes(lowerQuery) ||
    t.description.toLowerCase().includes(lowerQuery) ||
    t.category.toLowerCase().includes(lowerQuery)
  );
};

export const getTemplateById = (id) => {
  return DRAW_TEMPLATES.find(t => t.id === id);
};
